import React from 'react';
import classes from './AuctionHistoryCard.module.scss';

// Earlier auctions of this car, matched on the chassis number read off each
// auction sheet (GET /cars/auction-history/:id, filled by the backend's
// chassisOcrWorker). `history` is the response's data; null while loading or
// when the request failed.

const STATUS_TEXT = {
    pending: "This car's auction sheet hasn't been read yet. Its auction history will appear here shortly.",
    no_sheet: "There's no auction sheet for this listing, so its auction history can't be checked.",
    not_checked: "This listing is older than our auction sheet checks, so its auction history isn't available.",
    not_found: "We couldn't read a chassis number from this car's auction sheet, so it can't be matched to other auctions.",
    unconfirmed: "The chassis number on this sheet couldn't be confirmed, so we haven't matched it to other auctions.",
};

// Auction dates are calendar days ("2026/08/20"), read as [year, month, day].
const dateParts = (value) => {
    const m = String(value || '').match(/^(\d{4})\D(\d{1,2})\D(\d{1,2})/);
    return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
};

// Built from the parts: new Date("2026-08-20") is midnight UTC, which shows as
// the 19th west of UTC.
const formatDate = (value) => {
    const d = dateParts(value);
    if (!d) return value || '-';
    return new Date(d[0], d[1] - 1, d[2])
        .toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// A day as one comparable number: 2026/08/20 -> 20260820.
const dayNumber = (value) => {
    const d = dateParts(value);
    return d ? d[0] * 10000 + d[1] * 100 + d[2] : null;
};

// Today in Japan, where the auctions are held (UTC+9, no daylight saving).
const japanToday = () => {
    const now = new Date(Date.now() + 9 * 36e5);
    return now.getUTCFullYear() * 10000 + (now.getUTCMonth() + 1) * 100 + now.getUTCDate();
};

// Results are scraped before the auction and rarely updated after it, so past
// listings mostly still say "Auction not started". An auction that's over,
// with the car put up again on a later day, is shown as unsold.
const resultText = (appearance, lastDay, today) => {
    const day = dayNumber(appearance.auctionDate);
    const cameBack = day !== null && day < today && day < lastDay;
    if (cameBack && /^auction not started$/i.test(String(appearance.result || '').trim())) return 'Unsold';
    return appearance.result || '-';
};

const Summary = ({ history }) => {
    const { count, daysSincePrevious, appearances } = history;
    if (count <= 1) {
        return (
            <div className={`${classes.summary} ${classes.clean}`}>
                <i className="fas fa-check-circle"></i>
                <span>No other auctions of this car in our records.</span>
            </div>
        );
    }
    const repeated = count >= 3;
    // Appearances are oldest first; the others can also be later listings (a
    // car entered at two auctions the same week).
    const earlier = Math.max(0, appearances.findIndex((a) => a.isCurrent));
    const headline = repeated
        ? `Re-auctioned ${count - 1} times`
        : earlier ? 'Seen at auction before' : 'Also listed at another auction';
    return (
        <div className={`${classes.summary} ${repeated ? classes.alert : classes.notice}`}>
            <i className={repeated ? 'fas fa-exclamation-triangle' : 'fas fa-redo'}></i>
            <span>
                <strong>{headline}</strong>
                {' - '}at auction {count} times in our records
                {earlier && daysSincePrevious != null && daysSincePrevious >= 0
                    ? `, most recently ${daysSincePrevious} day${daysSincePrevious === 1 ? '' : 's'} before this one`
                    : ''}.
            </span>
        </div>
    );
};

const AuctionHistoryCard = ({ history, loading }) => {
    let body;
    if (loading) {
        body = <p className={classes.muted}>Checking auction records...</p>;
    } else if (!history) {
        body = <p className={classes.muted}>Auction history isn't available right now.</p>;
    } else if (history.status !== 'verified') {
        body = <p className={classes.muted}>{STATUS_TEXT[history.status] || STATUS_TEXT.pending}</p>;
    } else {
        const showPrice = history.appearances.some((a) => 'startPrice' in a);
        const lastDay = Math.max(0, ...history.appearances.map((a) => dayNumber(a.auctionDate) || 0));
        const today = japanToday();
        body = (
            <>
                <div className={classes.chassis}>
                    <span className={classes.chassisLabel}>Chassis number</span>
                    <span className={classes.chassisValue}>{history.chassisNo}</span>
                    <span className={classes.chassisNote}>read from the auction sheet</span>
                </div>
                <Summary history={history} />
                {history.mileageDrop && (
                    <div className={`${classes.summary} ${classes.alert}`}>
                        <i className="fas fa-tachometer-alt"></i>
                        <span><strong>Mileage went down between auctions.</strong> This can mean the odometer was changed or wound back.</span>
                    </div>
                )}
                {history.count > 1 && (
                    <div className={classes.tableWrap}>
                        <table className={classes.table}>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Auction</th>
                                    <th>Lot</th>
                                    <th>Grade</th>
                                    <th>Mileage</th>
                                    {showPrice && <th>Start</th>}
                                    <th>Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.appearances.map((a) => (
                                    <tr key={a.carId} className={a.isCurrent ? classes.current : ''}>
                                        <td>
                                            {formatDate(a.auctionDate)}
                                            {a.isCurrent && <span className={classes.thisTag}>This listing</span>}
                                        </td>
                                        <td>{a.auction || '-'}</td>
                                        <td>{String(a.lot || '').replace(/^No\.?\s*/i, '') || '-'}</td>
                                        <td>{a.score || '-'}</td>
                                        <td className={a.mileageDropped ? classes.drop : ''}>
                                            {a.mileage || '-'}
                                            {a.mileageDropped && <i className="fas fa-arrow-down" title="Lower than at an earlier auction"></i>}
                                        </td>
                                        {showPrice && <td>{a.startPrice || '-'}</td>}
                                        <td>{resultText(a, lastDay, today)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <p className={classes.note}>
                    Matched on the chassis number read from each auction sheet. Only auctions in our records are shown.
                </p>
            </>
        );
    }

    return (
        <div className={classes.card}>
            <h3 className={classes.title}>
                <i className="fas fa-history"></i>
                Auction History
            </h3>
            {body}
        </div>
    );
};

export default AuctionHistoryCard;
